import { FormEvent, useEffect, useMemo, useState } from 'react';

type ContactSubmissionStatus = 'new' | 'in_progress' | 'resolved' | 'archived';

type ContactSubmission = {
  id: string;
  name: string;
  email: string;
  message: string;
  status: ContactSubmissionStatus;
  created_at: string;
  updated_at: string;
};

const statusLabels: Record<ContactSubmissionStatus, string> = {
  new: 'New',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  archived: 'Archived'
};

function formatDate(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
}

function previewMessage(message: string) {
  if (message.length <= 96) {
    return message;
  }

  return `${message.slice(0, 93)}...`;
}

export function ContactSubmissionsPanel() {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [statusDraft, setStatusDraft] = useState<ContactSubmissionStatus>('new');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadSubmissions() {
      try {
        const response = await fetch('/api/admin/contact-submissions', {
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error('Contact submissions could not be loaded.');
        }

        const json = (await response.json()) as { contactSubmissions?: ContactSubmission[] };
        const nextSubmissions = Array.isArray(json.contactSubmissions) ? json.contactSubmissions : [];

        if (!isMounted) {
          return;
        }

        setSubmissions(nextSubmissions);

        if (nextSubmissions[0]) {
          setSelectedId(nextSubmissions[0].id);
          setStatusDraft(nextSubmissions[0].status);
        }
      } catch {
        if (!isMounted) {
          return;
        }

        setMessage('Contact submissions could not be loaded.');
        setIsError(true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadSubmissions();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedSubmission = useMemo(
    () => submissions.find((submission) => submission.id === selectedId) ?? submissions[0] ?? null,
    [selectedId, submissions]
  );

  function selectSubmission(submission: ContactSubmission) {
    setSelectedId(submission.id);
    setStatusDraft(submission.status);
    setMessage('');
    setIsError(false);
  }

  async function updateStatus(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!selectedSubmission) {
      return;
    }

    setIsUpdating(true);
    setMessage('');
    setIsError(false);

    try {
      const response = await fetch(`/api/admin/contact-submissions/${encodeURIComponent(selectedSubmission.id)}/status`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ status: statusDraft })
      });

      if (!response.ok) {
        throw new Error('Status could not be updated.');
      }

      const json = (await response.json()) as { contactSubmission?: ContactSubmission };

      if (!json.contactSubmission) {
        throw new Error('Status could not be updated.');
      }

      setSubmissions((currentSubmissions) =>
        currentSubmissions.map((submission) =>
          submission.id === json.contactSubmission?.id ? json.contactSubmission : submission
        )
      );
      setSelectedId(json.contactSubmission.id);
      setStatusDraft(json.contactSubmission.status);
      setMessage('Status updated.');
    } catch {
      setMessage('Status could not be updated.');
      setIsError(true);
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <section
      className="admin-panel contact-submissions-panel message-ledger"
      data-testid="message-ledger"
      aria-labelledby="contact-submissions-title"
    >
      <div className="contact-submissions-heading">
        <div>
          <p className="eyebrow">Inbox</p>
          <h2 id="contact-submissions-title">Contact Submissions</h2>
          <p>Review seeker inquiries and track follow-up status.</p>
        </div>
        <span className="ledger-count">{submissions.length} total</span>
      </div>

      {isLoading ? <p role="status">Loading contact submissions...</p> : null}
      {!isLoading && submissions.length === 0 && !isError ? <p>No contact submissions yet.</p> : null}
      {message ? <p role={isError ? 'alert' : 'status'}>{message}</p> : null}

      {submissions.length > 0 ? (
        <div className="contact-submissions-grid">
          <div className="contact-submissions-list" aria-label="Contact submission list">
            {submissions.map((submission) => (
              <button
                className={submission.id === selectedSubmission?.id ? 'contact-submission-row active' : 'contact-submission-row'}
                key={submission.id}
                type="button"
                onClick={() => selectSubmission(submission)}
              >
                <span>
                  <strong>{submission.name}</strong>
                  <small>{submission.email}</small>
                </span>
                <span className="status-pill status-seal">{statusLabels[submission.status]}</span>
                <small>{formatDate(submission.created_at)}</small>
                <p>{previewMessage(submission.message)}</p>
              </button>
            ))}
          </div>

          {selectedSubmission ? (
            <article className="contact-submission-detail">
              <header>
                <div>
                  <p className="eyebrow">Selected Message</p>
                  <h3>{selectedSubmission.name}</h3>
                  <a href={`mailto:${selectedSubmission.email}`}>{selectedSubmission.email}</a>
                </div>
                <span className="status-pill status-seal">{statusLabels[selectedSubmission.status]}</span>
              </header>
              <dl>
                <div>
                  <dt>Received</dt>
                  <dd>{formatDate(selectedSubmission.created_at)}</dd>
                </div>
                <div>
                  <dt>Updated</dt>
                  <dd>{formatDate(selectedSubmission.updated_at)}</dd>
                </div>
              </dl>
              <p className="contact-submission-message">{selectedSubmission.message}</p>
              <form className="admin-form status-form" onSubmit={updateStatus}>
                <label>
                  Status
                  <select
                    name="status"
                    value={statusDraft}
                    onChange={(event) => setStatusDraft(event.target.value as ContactSubmissionStatus)}
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <button className="button-primary" type="submit" disabled={isUpdating}>
                  {isUpdating ? 'Updating Status' : 'Update Status'}
                </button>
              </form>
            </article>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
